import { useState, useMemo } from 'react';
import { useDebouncedState } from 'utils/hooks/useDebouncedState';
import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { useClientPaginatedRows } from './useClientPaginatedRows';
import { useSortRows } from './useSortRows';

type UseClientPaginatedStateProps<DataType> = {
  rows: DataType[];
  sortedBy: keyof DataType | string;
  // whether or not the rows are paginated. Defaults to true.
  isPaginated?: boolean;
  // Used to filter the rows. If no function is provided, all rows will always be returned.
  filterFn?: (row: DataType, filter: string) => boolean;
  // Default value for the filter input
  defaultFilterValue?: string;
};

/**
 * useClientPaginatedState
 * Custom hook that can be used to manage state for client-side paginated tables.
 * Right now, it covers:
 * - sorting
 * - pagination
 * - filtering based on a provided filter function
 *
 * If you need a simpler hook that just does a part of the effort, take a look at:
 * - /src/ui/table/hooks/useSortRows.tsx: for sorting only
 * - /src/ui/table/hooks/useClientPaginatedRows.tsx: for pagination only
 */
export const useClientPaginatedState = <DataType extends Record<string, any>>({
  rows,
  sortedBy: controlledSortedBy,
  isPaginated = true,
  filterFn,
  defaultFilterValue = '',
}: UseClientPaginatedStateProps<DataType>) => {
  /** -- table header & rows state --  */
  const [sortedBy, setSortedBy] = useState(controlledSortedBy);
  const [sortDirection, setSortDirection] = useState<string | SORT_DIRECTIONS>(
    SORT_DIRECTIONS.DESCENDING,
  );

  /** -- filter state --  */
  const [debouncedFilter, filter, setFilter] = useDebouncedState(defaultFilterValue, 500);

  /** -- pagination state --  */
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  /** -- sorting logic -- */

  const filteredRows = useMemo(
    () =>
      filterFn && debouncedFilter.length
        ? rows.filter((row) => filterFn(row, debouncedFilter))
        : rows,
    [filterFn, debouncedFilter, rows],
  );
  const sortedRows = useSortRows(filteredRows, sortedBy, sortDirection);

  /** -- pagination logic -- */
  const paginatedRows = useClientPaginatedRows(sortedRows, currentPage, itemsPerPage);
  const numItems = useMemo(() => sortedRows.length, [sortedRows.length]);

  return {
    currentPage,
    debouncedFilter,
    filter,
    itemsPerPage,
    numItems,
    rows: isPaginated ? paginatedRows : sortedRows,
    setCurrentPage,
    setFilter,
    setItemsPerPage,
    setSortDirection,
    setSortedBy,
    sortDirection,
    sortedBy,
  };
};
